import { type DisplayInfo, type TagId } from '@blaze-shared/api-contracts-domain';
import { isColumnId, isTagId } from '@blaze-shared/schema-domain';

import { formulaParser } from '../lezer-syntax-parser';

import { buildTokensTree, getAllTokens } from './syntax-tree-builder';

export interface FormulaDisplayAlias {
  alias: string;
  rawAlias?: string;
  value: string;
}

export interface FormatFormulaForDisplayOptions {
  aliases?: FormulaDisplayAlias[];
  displayInfos?: DisplayInfo[];
  resolveColumnId?: (columnId: string) => string | undefined;
  resolveTagId?: (tagId: TagId) => string | undefined;
}

const sortByLength = (aliases: FormulaDisplayAlias[]): FormulaDisplayAlias[] =>
  [...aliases].sort((a, b) => b.value.length - a.value.length);

const stripVariablePrefix = (name: string): string =>
  name.startsWith('$$') || name.startsWith('@@')
    ? name.replace(/^(@@|\$\$)/, '').replace(/;$/, '')
    : name;

export const convertDisplayInfosToAliases = (
  displayInfos: DisplayInfo[] | undefined,
): FormulaDisplayAlias[] => {
  if (!displayInfos?.length) {
    return [];
  }

  return displayInfos
    .filter((info) => info.isAutoCompleted && info.value)
    .map((info) => ({ alias: info.display, rawAlias: info.display, value: info.value }));
};

const formatIndexer = (
  indexer: string,
  aliases: FormulaDisplayAlias[],
  options: Pick<FormatFormulaForDisplayOptions, 'resolveColumnId' | 'resolveTagId'>,
): string => {
  const raw = indexer.replace(/[[\]"]+/g, '');

  if (isColumnId(raw)) {
    const alias = aliases.find((entry) => entry.value.includes(raw));
    const resolved = alias
      ? alias.alias.replace(/[[\]"]+/g, '')
      : (options.resolveColumnId?.(raw) ?? raw);
    return `[${resolved}]`;
  }

  if (isTagId(raw)) {
    const resolved = options.resolveTagId?.(raw) ?? raw;
    return `[${resolved}]`;
  }

  return indexer;
};

const formatIndexers = (
  part: string,
  aliases: FormulaDisplayAlias[],
  options: Pick<FormatFormulaForDisplayOptions, 'resolveColumnId' | 'resolveTagId'>,
): string => {
  const regex = /\[[^\]]*]|\.\./g;
  let result = '';
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(part)) !== null) {
    result += part.slice(lastIndex, match.index);
    result += formatIndexer(match[0], aliases, options);
    lastIndex = match.index + match[0].length;
  }

  return result + part.slice(lastIndex);
};

export const formatVariableNameForDisplay = (
  variableName: string,
  options: FormatFormulaForDisplayOptions,
): string => {
  const aliases = sortByLength([
    ...(options.aliases ?? []),
    ...convertDisplayInfosToAliases(options.displayInfos),
  ]);
  const alias = aliases.find((entry) => variableName.startsWith(entry.value));

  if (!alias) {
    const stripped = stripVariablePrefix(variableName);

    if (isTagId(stripped)) {
      return options.resolveTagId?.(stripped) ?? stripped;
    }

    if (isColumnId(stripped)) {
      return options.resolveColumnId?.(stripped) ?? stripped;
    }

    return formatIndexers(stripped, aliases, options);
  }

  const displayAlias = alias.rawAlias ?? alias.alias;
  const remainder = variableName.slice(alias.value.length);
  const hasIndexation =
    remainder.length > 0 && (remainder.includes('[') || remainder.includes('..'));

  return hasIndexation ? displayAlias + formatIndexers(remainder, aliases, options) : displayAlias;
};

export const formatFormulaForDisplay = (
  code: string | null | undefined,
  options: FormatFormulaForDisplayOptions = {},
): string => {
  if (!code) {
    return '';
  }

  const tree = buildTokensTree(code, formulaParser);
  const variableTokens = getAllTokens(tree)
    .filter((token) => token.children.length === 0 && token.name === 'VariableName')
    .sort((a, b) => a.from - b.from);

  const segments: string[] = [];
  let cursor = 0;

  for (const token of variableTokens) {
    segments.push(
      code.slice(cursor, token.from),
      formatVariableNameForDisplay(token.text, options),
    );
    cursor = token.to;
  }

  segments.push(code.slice(cursor));

  return segments.join('');
};
